import {
  AspectRatio,
  Box,
  Container,
  Flex,
  HStack,
  Heading,
  Link,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";
import React, { useEffect } from "react";

import BlogCard from "../components/molecules/BlogCard";
import BookAMeetingBlogSection from "../components/molecules/BookAMeetingBlogSection";
import { Fb } from "../components/atoms/Fb";
import { LinkedIn } from "../components/atoms/LinkedIn";
import ProductCard from "../components/molecules/ProductCard";
import ReactDOM from "react-dom";
import RegularLayout from "../components/layouts/RegularLayout";
import { SEO } from "../components/molecules/Seo";
import { X } from "../components/atoms/Twitter";
import { fetchAllProducts } from "../queries/fetchAllProducts";
import { graphql } from "gatsby";
import styled from "styled-components";
import useAuth from "../components/useAuth";
import SignInBannerBlogSection from "../components/molecules/SignInBannerBlogSection";
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image";
import { RiCalendarEventLine } from "../components/customIcons";

const Div = styled.div`
  .wp-block-table {
    border: 1px solid #171923;
    tr {
      border: 1px solid #171923;
    }
    td {
      border: 1px solid #171923;
    }
    table {
      width: 100%;
    }
  }
  .ez-toc-title-container {
    font-size: 18px;
    font-weight: 600;
    padding-top: 24px;
  }

  h2.wp-block-heading,
  h3.wp-block-heading {
    font-size: 30px;
    font-weight: 700;
    color: #171923;
    margin: 1rem 0;
  }

  h4.wp-block-heading {
    font-size: 1rem;
    font-weight: 700;
    color: #171923;
    padding: 1rem 0.5rem;
    + p {
      padding: 0 8px;
    }
  }
  ez-toc-icon-toggle-span {
    display: none;
  }
  .postArticle {
    ol li {
      font-style: italic;
    }
    p,
    li {
      color: #4a5568;
      padding-bottom: 0.5rem;
    }
    a {
      color: #bb5d82;
      font-weight: 400;
      word-break: break-all;
      &:hover {
        text-decoration: underline;
      }
    }
    strong {
      font-weight: 400;
    }
    ul {
      padding: 8px 0;
    }
    li {
      padding-left: 0.5rem;
      margin-left: 2rem;
    }
  }
`;

const gradientStyle: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 66.5%)",
};
const blurOverlayStyle: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.5)", // Adjust the opacity as needed
  backdropFilter: "blur(3px)", // Adjust the blur radius as needed
};
const BlogPost = ({ data }: any) => {
  const categories = data.wpPost.categories.nodes.map((category: any) => ({
    name: category.name,
    slug: category.slug,
  }));

  const postTitle = data.wpPost.title;
  const postSlug = data.wpPost.slug;

  const breadcrumbs = [
    { label: "Home", link: "/" },
    { label: "Blog", link: "/blog/" },
    { label: `${categories[0].name}`, link: `/blog/${categories[0].slug}/` },
    { label: postTitle, link: `/blog/${categories[0].slug}/${postSlug}/` },
  ];

  const previousPost = data.previous
    ? {
        slug: data.previous.slug,
        title: data.previous.title,
        date: data.previous.date,
        category: data.previous.categories.nodes,
        excerpt: data.previous.seo.metaDesc,
        image:
          data.previous.featuredImage?.node?.localFile?.childImageSharp
            ?.gatsbyImageData,
        author: data.previous.author?.node?.name,
        authorSlug: data.previous.author?.node?.slug,
      }
    : null;

  const nextPost = data.next
    ? {
        slug: data.next.slug,
        title: data.next.title,
        date: data.next.date,
        category: data.next.categories.nodes,
        excerpt: data.next.seo.metaDesc,
        image:
          data.next.featuredImage?.node?.localFile?.childImageSharp
            ?.gatsbyImageData,
        author: data.next.author?.node?.name,
        authorSlug: data.next.author?.node?.slug,
      }
    : null;

  const posts = data.allWpPost.nodes;

  const date = data.wpPost.date;
  const title = data.wpPost.title;
  const content = data.wpPost.content;
  const blogCategory = data.wpPost.categories.nodes;
  const formattedDate = new Date(
    data.wpPost.modified.slice(0, 10),
  ).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const mentionedProducts = data.wpPost.mentionedProducts.mentionedProducts;
  const products = fetchAllProducts();
  const { loggedIn, isUnlicensedUser, isCustomerDefault, isBankAcc } =
    useAuth();

  const pharmas =
    mentionedProducts && mentionedProducts.length > 0
      ? mentionedProducts.some((product: any) => product.ispharmaceutical)
      : false;

  const firstH2Index = content.indexOf("<h2");
  const beforeFirstH2 = content.substring(0, firstH2Index);
  const secondH2Index = content.indexOf("<h2", firstH2Index + 1);
  const afterFirstH2 = content.substring(
    firstH2Index,
    secondH2Index !== -1 ? secondH2Index : undefined,
  );
  const afterSecondH2 =
    secondH2Index !== -1 ? content.substring(secondH2Index) : "";

  const ShareButtons = ({ title, description }: any) => {
    const categorySlug = blogCategory?.map((category: any) => category.slug);
    const postSlug = data.wpPost.slug;
    const fullURL =
      "https://www.medsupplysolutions.com/blog/" +
      categorySlug +
      "/" +
      postSlug +
      "/";

    return (
      <Stack display={"flex"} flexFlow={"row"}>
        <FacebookShareButton url={fullURL} quote={description}>
          <Fb w="28px" h="29px" mr={6} href={fullURL} />
        </FacebookShareButton>
        <TwitterShareButton url={fullURL} title={title}>
          <X w="28px" h="29px" mr={6} href={fullURL} />
        </TwitterShareButton>
        <LinkedinShareButton rel="canonical" url={fullURL} title={title}>
          <LinkedIn w="28px" h="29px" mr={6} href={fullURL} />
        </LinkedinShareButton>
      </Stack>
    );
  };

  useEffect(() => {
    const renderCustomComponent = () => {
      const faqElement = document.getElementById("h-faq");
      const referencesElement = document.getElementById("h-references");
      const faqsElement = document.getElementById("h-faqs");
      const referenceElement = document.getElementById("h-reference");

      const elements = [
        faqElement,
        referencesElement,
        faqsElement,
        referenceElement,
      ];
      const foundElement = elements.find((element) => element !== null);

      if (foundElement && foundElement.parentNode) {
        const container = document.createElement("div");

        ReactDOM.render(<BookAMeetingBlogSection />, container);

        foundElement.parentNode.insertBefore(container, foundElement);
      } else {
        const container = document.createElement("div");
        const targetDiv = document.querySelector(".css-1345fpq");

        if (targetDiv) {
          targetDiv.appendChild(container);

          ReactDOM.render(<BookAMeetingBlogSection />, container);
        }
      }
    };

    renderCustomComponent();
  }, []);

  useEffect(() => {
    const targetElement = document.getElementById("h-key-takeaways");

    const removeRenderedComponent = () => {
      const renderedContainer = document.getElementById(
        "sign-in-banner-container",
      );
      if (renderedContainer && renderedContainer.parentNode) {
        renderedContainer.parentNode.removeChild(renderedContainer);
      }
    };

    if (!loggedIn) {
      if (targetElement && targetElement.parentNode) {
        const container = document.createElement("div");
        container.id = "sign-in-banner-container";

        ReactDOM.render(<SignInBannerBlogSection />, container);
        targetElement.parentNode.insertBefore(container, targetElement);
      }
    } else {
      removeRenderedComponent();
    }
  }, [loggedIn]);

  const imageData =
    data.wpPost.featuredImage?.node?.localFile?.childImageSharp
      ?.gatsbyImageData;

  const image = imageData ? getImage(imageData) : null;

  return (
    <Div>
      <RegularLayout
        px={4}
        backgroundColor="#fff"
        maxWidth={{ base: "3xl", md: "7xl" }}
        crumbs={breadcrumbs}
      >
        <Container mt={10} pb={20} maxW={{ base: "3xl", md: "7xl" }}>
          <Box position="relative">
            <Box position="relative" borderRadius={8} overflow="hidden">
              <AspectRatio ratio={{ base: 16 / 9, lg: 21 / 9 }}>
                <Box position="relative">
                  {image ? (
                    <GatsbyImage
                      image={image}
                      alt="Blog Featured Image"
                      objectFit="cover"
                      style={{ height: "100%", width: "100%" }}
                    />
                  ) : (
                    <StaticImage
                      rel="preload"
                      src="../images/placeholderBlog.webp"
                      alt="Blog Featured Image"
                      objectFit="cover"
                      layout="fullWidth"
                      style={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: "transparent !important",
                      }}
                    />
                  )}
                  <Box style={gradientStyle} />
                  <Box style={blurOverlayStyle} />
                </Box>
              </AspectRatio>
            </Box>
          </Box>

          <Flex
            px={{ base: 0, md: 6 }}
            mt={{ base: "-180px", sm: "-240px", md: "-400px" }}
            direction="column"
            flex="1"
          >
            <Stack
              direction={{ base: "column", lg: "row" }}
              spacing={{ base: "0", lg: "120px" }}
              flex="1"
            >
              <VStack zIndex={100} alignItems={"flex-start"}>
                <Heading
                  as="h1"
                  fontSize={{ base: "2xl", lg: "4xl" }}
                  fontWeight={"bold"}
                  textAlign={"left"}
                  lineHeight={"40px"}
                >
                  {title}
                </Heading>

                <Link href={`/blog/author/${posts[0].author?.node.slug}/`}>
                  <Text color="#BB5D82" mt={6}>
                    {posts[0].author?.node.name}
                  </Text>
                </Link>

                <Text textAlign={"left"} fontSize={"sm"}>
                  Last Updated On: {formattedDate}
                </Text>
                <Text
                  className={"postArticle"}
                  mt={{ base: 6, md: 4 }}
                  mb={{ base: 8, md: 0 }}
                  dangerouslySetInnerHTML={{ __html: beforeFirstH2 }}
                ></Text>
                <Text
                  className={"postArticle"}
                  mt={{ base: 6, md: 1.5 }}
                  mb={{ base: 8, md: 0 }}
                  dangerouslySetInnerHTML={{ __html: afterFirstH2 }}
                />

                {!loggedIn && pharmas ? (
                  <Link
                    href="https://medsupplysolutions.youcanbook.me"
                    target="_blank"
                    rel="noopener"
                    mx={"auto"}
                  >
                    <Box
                      justifyContent="center"
                      w={{ base: "340px", sm: "440px", md: "640px" }}
                      h="200px"
                    >
                      <StaticImage
                        rel="preload"
                        src="../images/mss-banner.webp"
                        alt="Banner Image"
                        layout="fullWidth"
                        style={{
                          height: "100%",
                          width: "100%",
                          backgroundColor: "transparent !important",
                        }}
                        imgStyle={{
                          objectFit: "contain",
                          borderRadius: "8px",
                        }}
                      />
                    </Box>
                  </Link>
                ) : loggedIn &&
                  pharmas &&
                  (isUnlicensedUser || isCustomerDefault || isBankAcc) ? (
                  <Link
                    href="https://medsupplysolutions.youcanbook.me"
                    target="_blank"
                    rel="noopener"
                    mx={"auto"}
                  >
                    <Box
                      justifyContent="center"
                      w={{ base: "340px", sm: "440px", md: "640px" }}
                      h="200px"
                    >
                      <StaticImage
                        rel="preload"
                        src="../images/mss-non-licensed-registered-users.webp"
                        alt="Banner Image"
                        layout="fullWidth"
                        style={{
                          height: "100%",
                          width: "100%",
                          backgroundColor: "transparent !important",
                        }}
                        imgStyle={{
                          objectFit: "contain",
                          borderRadius: "8px",
                        }}
                      />
                    </Box>
                  </Link>
                ) : (
                  <Box
                    bg={
                      mentionedProducts && mentionedProducts.length > 0
                        ? "#F7FAFC"
                        : "white"
                    }
                    display={
                      mentionedProducts && mentionedProducts.length > 0
                        ? "flex"
                        : "none"
                    }
                    w={{ base: "100%", md: "700px" }}
                    p={{ base: 2, md: 5 }}
                    mx="auto"
                    flexFlow={{ base: "column", md: "row" }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    {products?.map((item: any, index: any) => {
                      const mentionedProduct = mentionedProducts?.find(
                        (mp: any) => mp.productId == item.databaseId,
                      );

                      const imageData =
                        item.featuredImage?.node?.localFile?.childImageSharp
                          ?.gatsbyImageData;
                      const altText =
                        item.featuredImage?.node?.altText ||
                        "Blog Featured Image";

                      if (mentionedProduct) {
                        return (
                          <ProductCard
                            key={index}
                            salePrice={item?.salePrice}
                            heading="h3"
                            width={"312px"}
                            marginBottom={{ base: 2, md: "0" }}
                            imageData={imageData}
                            altText={altText}
                            name={item.name}
                            price={item.price}
                            slug={item?.slug!}
                            productId={item.databaseId}
                            activeSubstance={item.allPaActiveIngredient?.nodes}
                            regularPrice={item.regularPrice}
                            isRelatedProducts={false}
                            stockStatus={item.stockStatus}
                            tierPrice={item?.tierPrice?.tierPrice}
                            isProductOnPromotion={
                              item?.isOnPromotion?.isonpromotion
                            }
                          />
                        );
                      }
                    })}
                  </Box>
                )}

                <Text
                  className={"postArticle"}
                  mt={{ base: 6, md: 4 }}
                  mb={{ base: 8, md: 0 }}
                  dangerouslySetInnerHTML={{ __html: afterSecondH2 }}
                />
              </VStack>
              <Stack
                display={"flex"}
                alignItems={"center"}
                justify={"flex-start"}
                flexDir={{ base: "row", lg: "column" }}
              >
                <Box
                  display="flex"
                  flexDir={"column"}
                  alignItems="center"
                  justifyContent="center"
                  as="aside"
                  role="complementary"
                  bg="bg.accent.default"
                  width={{ base: "full", lg: "xs" }}
                  alignSelf="start"
                  position={{ base: "unset", lg: "sticky" }}
                >
                  <VStack
                    mx="auto"
                    gap="8px"
                    mt={{ base: 10, lg: 0 }}
                    alignItems={{ base: "center", lg: "flex-start" }}
                    w={"100%"}
                  >
                    <Link
                      href="https://www.medsupplysolutions.com/promo/"
                      pb="12px"
                    >
                      <StaticImage
                        rel="preload"
                        src="../images/promo-banner-blog.webp"
                        alt="promo-banner-post"
                        style={{ backgroundColor: "transparent !important" }}
                      />
                    </Link>
                    <Text
                      h={8}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      bg="#FFE9F3"
                      color="#7F3451"
                      fontWeight="500"
                      borderRadius="6px"
                      position="relative"
                      fontSize="16px"
                      textAlign="center"
                      px="12px"
                    >
                      {blogCategory.map((post: any, index: any) => (
                        <>
                          <Link key={index} href={`/blog/${post.slug}/`}>
                            {post.name.toUpperCase()}
                          </Link>
                        </>
                      ))}
                    </Text>
                    {/* <Link href={`/blog/author/${authorSlug}`}>
                      <Text fontSize="14px" color="#BB5D82">
                        {author}
                      </Text>
                    </Link> */}
                    <HStack>
                      <RiCalendarEventLine w="16px" h="16px" fill="#fff" />
                      <Text fontSize="14px" color="#4A5568">
                        {date}
                      </Text>
                    </HStack>
                  </VStack>
                  <Stack spacing={12}>
                    {nextPost && (
                      <VStack alignItems={"flex-start"} mt={12} spacing={6}>
                        <Text fontSize={"2xl"} fontWeight={700}>
                          Previous Article
                        </Text>
                        <BlogCard
                          key={nextPost.slug}
                          authorSlug={nextPost.authorSlug}
                          backgroundColor={"#fff"}
                          date={date}
                          title={nextPost.title}
                          author={nextPost.author?.node?.name}
                          imageData={nextPost.image}
                          slug={
                            "/blog/" +
                            nextPost.category.map((item: any) => item.slug) +
                            "/" +
                            nextPost.slug
                          }
                          category={nextPost.category.map((item: any) =>
                            item.name.toUpperCase(),
                          )}
                          description={nextPost.excerpt}
                          input={false}
                        />
                      </VStack>
                    )}

                    {previousPost && (
                      <VStack alignItems={"flex-start"} spacing={6}>
                        <Text fontSize={"2xl"} fontWeight={700}>
                          Next Article
                        </Text>
                        <BlogCard
                          key={previousPost.slug}
                          backgroundColor={"#fff"}
                          authorSlug={previousPost.authorSlug}
                          date={date}
                          title={previousPost.title}
                          author={previousPost.author?.node?.name}
                          slug={
                            "/blog/" +
                            previousPost.category.map(
                              (item: any) => item.slug,
                            ) +
                            "/" +
                            previousPost.slug
                          }
                          imageData={previousPost.image}
                          category={previousPost.category.map((item: any) =>
                            item.name.toUpperCase(),
                          )}
                          description={previousPost.excerpt}
                          input={false}
                        />
                      </VStack>
                    )}

                    <VStack alignItems={"flex-start"} spacing={6}>
                      <Text
                        fontWeight={400}
                        lineHeight={"140%"}
                        color={"#4A5568"}
                      >
                        Share this article
                      </Text>
                      <ShareButtons url={""} title={title} description={""} />
                      <Link
                        href="https://www.medsupplysolutions.com/shop/"
                        pb="12px"
                      >
                        <StaticImage
                          rel="preload"
                          src="../images/practice-essentials.webp"
                          style={{
                            height: "unset",
                            width: "100%",
                            backgroundColor: "transparent !important",
                          }}
                          alt="blog-post"
                        />
                      </Link>
                    </VStack>
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Flex>
        </Container>
      </RegularLayout>
    </Div>
  );
};

export default BlogPost;

export const Head = ({ data }: any) => (
  <SEO
    title={data.wpPost.seo.title}
    description={data.wpPost.seo.metaDesc}
    featuredImage={
      data.wpPost.featuredImage?.node?.localFile?.childImageSharp
        ?.gatsbyImageData
    }
    content="article"
  />
);

export const query = graphql`
  query ($id: String!, $previousPostId: String, $nextPostId: String) {
    wpPost(id: { eq: $id }) {
      mentionedProducts {
        mentionedProducts {
          productId
          ispharmaceutical
        }
      }
      title
      modified
      slug
      seo {
        title
        metaDesc
      }
      featuredImage {
        node {
          localFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
          altText
        }
      }
      categories {
        nodes {
          name
          slug
        }
      }
      tags {
        nodes {
          name
        }
      }
      content
      excerpt

      date(formatString: "MMMM DD, YYYY")
    }
    previous: wpPost(id: { eq: $previousPostId }) {
      slug
      title
      date
      excerpt
      author {
        node {
          name
          slug
        }
      }
      seo {
        metaDesc
      }
      isSticky
      author {
        node {
          name
        }
      }
      featuredImage {
        node {
          localFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
          altText
        }
      }
      categories {
        nodes {
          name
          slug
        }
      }
    }
    next: wpPost(id: { eq: $nextPostId }) {
      slug
      title
      date
      excerpt
      author {
        node {
          name
          slug
        }
      }
      seo {
        metaDesc
      }
      isSticky
      author {
        node {
          name
        }
      }
      featuredImage {
        node {
          localFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
          altText
        }
      }
      categories {
        nodes {
          name
          slug
        }
      }
    }

    allWpPost(sort: { date: DESC }, limit: 5) {
      nodes {
        title
        excerpt
        slug
        author {
          node {
            name
            slug
          }
        }
        date(formatString: "MMMM DD, YYYY")
        featuredImage {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH)
              }
            }
            altText
          }
        }
        tags {
          nodes {
            name
          }
        }
        categories {
          nodes {
            name
          }
        }
        tags {
          nodes {
            name
          }
        }
      }
    }
  }
`;
